/**
 * Clasificador único de tipo de archivo para adjuntos de chat, compartido
 * entre el compositor (modal de preview pre-envío), la burbuja de mensaje
 * ya enviado y el panel "Archivos" de la conversación — las tres
 * superficies deben mostrar el mismo icono/color para el mismo archivo.
 */
export type ChatFileKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'archive'
  | 'code'
  | 'markdown'
  | 'text'
  | 'binary'
  | 'generic';

export function isSemanticChatAudio(
  mediaKind: string | null | undefined,
  mimeType: string | null | undefined,
): boolean {
  return mediaKind === 'ATTACHED_AUDIO'
    || mediaKind === 'VOICE_NOTE'
    || !!mimeType?.toLowerCase().startsWith('audio/');
}

const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'weba']);
/**
 * Extensiones de imagen sin fallback previo por extensión — a diferencia de
 * pdf/word/excel/etc, la detección de imagen dependía 100% de `mimeType`
 * empezando con "image/". Formatos como .jfif no están registrados como
 * tipo MIME en muchos navegadores/SO y llegan con `File.type === ''`, así
 * que sin este fallback se clasifican como 'generic' en vez de 'image'.
 */
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'jfif', 'jif', 'jpe', 'pjpeg', 'pjp', 'png', 'gif', 'webp', 'avif', 'heic', 'heif', 'bmp']);
/** Subconjunto de IMAGE_EXTENSIONS que son variantes de JPEG por contenido — usado solo para inferir un mime concreto cuando el navegador no da ninguno (ver normalizeChatUploadMime). No usar IMAGE_EXTENSIONS completo ahí: un .png con mime vacío no es un JPEG. */
const JPEG_VARIANT_EXTENSIONS = new Set(['jfif', 'jif', 'jpe', 'pjpeg', 'pjp']);
const WORD_EXTENSIONS = new Set(['doc', 'docx']);
const EXCEL_EXTENSIONS = new Set(['xls', 'xlsx', 'csv']);
const POWERPOINT_EXTENSIONS = new Set(['ppt', 'pptx']);
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'tgz']);
const CODE_EXTENSIONS = new Set(['json', 'xml', 'yaml', 'yml']);
const BINARY_EXTENSIONS = new Set(['apk', 'exe', 'bin', 'dmg', 'msi']);

/** Color de acento por tipo, único punto de verdad para las tres superficies. */
export const CHAT_FILE_KIND_COLOR: Record<ChatFileKind, string> = {
  image: '#4da6ff',
  video: '#4da6ff',
  audio: '#4da6ff',
  pdf: '#da4938',
  word: '#2b5faa',
  excel: '#1d8a4a',
  powerpoint: '#e8792a',
  archive: '#a06be0',
  code: '#3fb8b8',
  // El acento se usa a la vez como color del label y como fondo tintado al 15%
  // (ver `tintedBackground` en core-ui-file-type-icon), asi que el texto compite
  // contra un fondo derivado de si mismo. Con el celeste anterior (#8bb8e0) el
  // contraste sobre la card clara quedaba en 1.84:1 y la etiqueta se leia
  // lavada; este azul lo lleva a 5.05:1, que cumple WCAG AA para texto chico.
  markdown: '#2c5f96',
  text: '#2c5f96',
  binary: '#7b8795',
  generic: '#7b8795',
};

export function resolveChatFileKind(
  fileName: string | null | undefined,
  mimeType?: string | null,
): ChatFileKind {
  const mime = (mimeType ?? '').toLowerCase();
  const extension = extensionOf(fileName);
  if (mime.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) return 'image';
  if (mime.startsWith('video/')) return 'video';

  if (mime.startsWith('audio/') || AUDIO_EXTENSIONS.has(extension)) return 'audio';
  if (extension === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (WORD_EXTENSIONS.has(extension) || mime.includes('word')) return 'word';
  if (
    EXCEL_EXTENSIONS.has(extension) ||
    mime.includes('spreadsheet') ||
    mime.includes('excel')
  )
    return 'excel';
  if (
    POWERPOINT_EXTENSIONS.has(extension) ||
    mime.includes('presentation') ||
    mime.includes('powerpoint')
  )
    return 'powerpoint';
  if (ARCHIVE_EXTENSIONS.has(extension) || mime.includes('zip') || mime.includes('compressed'))
    return 'archive';
  if (CODE_EXTENSIONS.has(extension) || mime.includes('json') || mime.includes('xml') || mime.includes('yaml'))
    return 'code';
  if (extension === 'md') return 'markdown';
  if (extension === 'txt' || mime === 'text/plain') return 'text';
  if (BINARY_EXTENSIONS.has(extension)) return 'binary';
  return 'generic';
}

/** Etiqueta corta para el badge del icono (ej. "PDF", "XLSX", "MP4"). */
export function chatFileTypeLabel(
  fileName: string | null | undefined,
  mimeType?: string | null,
): string {
  const extension = extensionOf(fileName);
  if (extension) return extension.toUpperCase();
  const subtype = (mimeType ?? '').split('/')[1]?.split(';')[0];
  return subtype ? subtype.toUpperCase() : 'FILE';
}

export function formatChatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** true para los únicos tipos que se pueden previsualizar en lugar de mostrar un icono. */
export function canPreviewChatFileKind(kind: ChatFileKind): boolean {
  return kind === 'image' || kind === 'video';
}

/**
 * Categorías granulares del filtro de la pestaña "Archivos" (MOMSG-1126).
 * A propósito NO cubre las 4 `ChatFileKind` restantes (audio, video,
 * markdown, binary) — decisión confirmada con el usuario, ver plan.
 */
export type ChatFileFilterCategory =
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive'
  | 'text'
  | 'image';

/** Etiqueta en español para el dropdown de filtro por categoría. */
export const CHAT_FILE_FILTER_CATEGORY_LABEL_ES: Record<ChatFileFilterCategory, string> = {
  pdf: 'PDF',
  document: 'Word',
  spreadsheet: 'Excel',
  presentation: 'PowerPoint',
  archive: 'Comprimidos',
  text: 'Texto',
  image: 'Imágenes',
};

function extensionOf(fileName: string | null | undefined): string {
  if (!fileName) return '';
  const clean = fileName.split('?')[0] ?? fileName;
  const lastDot = clean.lastIndexOf('.');
  return lastDot >= 0 ? clean.slice(lastDot + 1).toLowerCase() : '';
}

/**
 * Normaliza el MIME declarado por el navegador para el upload de chat.
 *
 * Varios navegadores/SO no tienen ciertas extensiones registradas
 * (`.md`, `.jfif`) y devuelven `File.type` vacío — confiar ciegamente en
 * eso hace que el backend rechace el archivo (mime en blanco, o cae al
 * genérico `application/octet-stream`, que no está en ninguna lista
 * blanca). Inferimos por extensión cuando el navegador no da un mime útil.
 *
 * IMPORTANTE sobre `.md`: se normaliza a `text/plain`, NO a
 * `text/markdown`. El backend valida el contenido real subido mediante
 * content-sniffing (`ChatMediaProcessor.detectMime`), y ese sniffer NUNCA
 * puede distinguir Markdown de texto plano — cualquier texto sin bytes
 * nulos se detecta como `text/plain`. Si acá declarásemos `text/markdown`,
 * el backend compararía "detectado text/plain" contra "declarado
 * text/markdown", no coincidirían, y el archivo se rechazaría con
 * MEDIA_TYPE_MISMATCH — recién después de subir todos los bytes a S3.
 * `text/plain` es el único valor con el que el round-trip declarado↔detectado
 * puede coincidir, y ya está permitido en ambas listas blancas.
 */
export function normalizeChatUploadMime(
  fileName: string | null | undefined,
  browserMime: string | null | undefined,
): string {
  const mime = (browserMime ?? '').trim();
  const extension = extensionOf(fileName);
  if (extension === 'md') return 'text/plain';
  if (JPEG_VARIANT_EXTENSIONS.has(extension) && !mime.startsWith('image/')) return 'image/jpeg';
  if (mime && mime !== 'application/octet-stream') return mime;
  return mime || 'application/octet-stream';
}
